/**
 * Résumé extraction — LinkedIn-first, pattern-driven.
 *
 * Runs entirely in the browser (ADR-016). Nothing here transmits; it produces
 * the structure that redact.js then narrows to a transmission payload.
 *
 * ── Why not positional parsing ────────────────────────────────────────────
 * Probing six real LinkedIn exports showed the line order after "Experience"
 * is NOT stable:
 *
 *   A: company → title → dates → location
 *   B: company → "Page 1 of 4" → "11 years 3 months" → title → dates → location
 *   C: degree → "Page 1 of 4" → company → dates → location → TITLE
 *   D: "Page 1 of 6" → company → "4 years 6 months" → title → dates → location
 *
 * In C the dates precede the title. "The line after Experience is the job
 * title" is wrong on real data, so this parser identifies the lines it CAN
 * recognise by pattern (dates, durations, pagination, locations), then asks the
 * O*NET resolver which of the remaining lines actually looks like an
 * occupation. The taxonomy recognises titles; company names do not resolve.
 *
 * ── Acquisition order (ADR-025) ───────────────────────────────────────────
 * Deterministic parse → model-assisted extraction → derivation from what we
 * already hold → ask the user. Each question we ask is a small failure of the
 * AI-first promise, so we ask only what genuinely cannot be derived.
 */

const MONTH =
  "(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec|January|February|March|April|June|July|August|September|October|November|December)";

const RE = {
  page: /^Page\s+\d+\s+of\s+\d+$/i,
  dateRange: new RegExp(`${MONTH}\\s+\\d{4}\\s*[-–—]\\s*(?:Present|${MONTH}\\s+\\d{4})`, "i"),
  duration: /^\s*\d+\s+(?:year|yr|month|mo)s?(?:\s+\d+\s+(?:month|mo)s?)?\s*$/i,
  durationInline: /\((\d+)\s*(?:year|yr)s?(?:\s*(\d+)\s*(?:month|mo)s?)?/i,
  monthsOnly: /\((\d+)\s*(?:month|mo)s?\)/i,
  location: /^[A-Z][\w.'-]+(?:\s+[\w.'-]+)*,\s*[A-Z][\w.'-]+(?:,\s*[A-Za-z\s]+)?$/,
  bullet: /^[•▪·\-–—*]\s+/,
};

const MONTH_INDEX = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9,
  oct: 10, nov: 11, dec: 12,
};
const monthNumber = (word) => MONTH_INDEX[String(word || "").slice(0, 4).toLowerCase().replace(/[^a-z]/g, "").slice(0, 4)]
  ?? MONTH_INDEX[String(word || "").slice(0, 3).toLowerCase()];

/**
 * A date range as its two endpoints, not just a duration.
 *
 * `durationMonths()` above reads LinkedIn's own "(4 yrs 6 mos)" parenthetical,
 * which a Word CV does not have — it writes "January 2020–Present" and expects
 * you to do the arithmetic. Doing it is what lets roles be ordered by recency
 * (ADR-030's open caller problem) rather than by how well the index recognised
 * their titles.
 *
 * @returns {null|{start:{y,m}, end:{y,m}|null, current:boolean, months:number}}
 */
export function parseDateRange(line, now = new Date()) {
  const re = new RegExp(
    `(${MONTH})\\s+(\\d{4})\\s*[-–—]\\s*(?:(Present|Current)|(${MONTH})\\s+(\\d{4}))`, "i");
  const m = re.exec(String(line || ""));
  if (!m) return null;
  const start = { y: Number(m[2]), m: monthNumber(m[1]) };
  if (!start.m || !start.y) return null;
  const current = Boolean(m[3]);
  const end = current
    ? { y: now.getFullYear(), m: now.getMonth() + 1 }
    : { y: Number(m[5]), m: monthNumber(m[4]) };
  if (!end.m || !end.y) return null;
  const months = (end.y - start.y) * 12 + (end.m - start.m);
  if (months < 0) return null;
  // `index`/`raw` let a caller strip the date out of a line that also carries the
  // title and the company — see datedBlocks.
  return { start, end: current ? null : end, current, months, index: m.index, raw: m[0] };
}

/**
 * Split "Director, Operations Transformation — Harbor National Bank".
 *
 * The old `offer()` normalised the em dash to a SPACE, so title and company were
 * handed to the resolver as one six-token candidate. The junk diluted the match,
 * most candidates fell under the 0.35 admission bar — which is why a three-role
 * CV yielded one role — and "Financial Services" inside a title pulled a
 * transformation director toward Financial Managers.
 *
 * A comma is NOT a separator: "Director, Operations Transformation" is one title.
 *
 * With MORE than two segments the middle is the scope or the client, not the
 * employer — "Principal Data Engineer | Lakehouse Observability | Fidelity" — so
 * the title is the first segment and the employer the last. Taking everything
 * after the first separator as the company produced
 * "Lakehouse Observability & Experimentation | Fidelity Investments" as an
 * employer name, which is not one.
 */
export function splitTitleCompany(line) {
  const raw = String(line || "").trim();
  const parts = raw
    .split(/\s*(?:[–—‒|·•]|\s-\s|\s+at\s+|\s+@\s+)\s*/i)
    .map((t) => t.replace(/^[\s,;.]+|[\s,;.]+$/g, "").trim())
    .filter(Boolean);

  if (parts.length < 2) return { title: raw, company: null, scope: null };
  return {
    title: parts[0],
    company: parts[parts.length - 1],
    // What sat between the two: the programme, the product, or the client. Real
    // task evidence, so it is kept rather than folded into a company name.
    scope: parts.length > 2 ? parts.slice(1, -1).join(" · ") : null,
  };
}

const SECTIONS = [
  "Contact", "Top Skills", "Skills", "Languages", "Certifications",
  "Honors-Awards", "Publications", "Summary", "Experience", "Education",
  "Volunteer", "Projects", "Interests", "Patents", "Courses",
];

/**
 * Section headings, as people actually write them.
 *
 * `splitSections` used to test `line.toLowerCase() === section.toLowerCase()`.
 * A real CV headed PROFESSIONAL EXPERIENCE / CORE TECHNICAL SKILLS / ACADEMIC
 * SUMMARY matched exactly ONE of them — Certifications — so 39 of its 44 lines
 * fell into `_preamble`, `sections.Experience` came back empty, and the entire
 * date-anchored parser was unreachable. A Principal Data Engineer with fifteen
 * years in lakehouse infrastructure resolved to *Search Marketing Strategists*.
 *
 * ORDER IS LOAD-BEARING. "ACADEMIC SUMMARY" contains both "academic" and
 * "summary"; Education has to win, so it is listed first.
 */
const SECTION_MATCHERS = [
  ["Top Skills",     /\btop\s+skills\b/],
  ["Education",      /\b(education|educational|academic|qualifications?|degrees?)\b/],
  ["Certifications", /\b(certification|certificate|certifications|licen[cs]es?|credentials?|accreditations?)\b/],
  ["Languages",      /\blanguages?\b/],
  ["Publications",   /\b(publications?|papers?|patents?)\b/],
  ["Projects",       /\bprojects?\b/],
  ["Volunteer",      /\b(volunteer|volunteering|community)\b/],
  ["Courses",        /\b(courses?|training|coursework)\b/],
  ["Experience",     /\b(experience|employment|work\s+history|career\s+history|professional\s+background|positions?\s+held)\b/],
  ["Skills",         /\b(skills?|competenc(y|ies)|expertise|proficienc(y|ies)|capabilit(y|ies)|technologies|toolkit)\b/],
  ["Summary",        /\b(summary|profile|objective|overview|about\s+me|highlights)\b/],
  ["Contact",        /\bcontact\b/],
];

/**
 * Is this line a heading, or a sentence that happens to contain a heading word?
 *
 * The cost of getting this wrong runs both ways — miss a heading and the parser
 * loses the document's structure; invent one and a bullet becomes a section
 * boundary. So: short, unpunctuated, and not itself a dated role header.
 */
function headingFor(line, fuzzy) {
  const raw = String(line || "").replace(/[:–—|]+\s*$/, "").trim();
  if (!raw || raw.length > 48) return null;
  if (/[.;!?]$/.test(raw)) return null;               // a sentence, not a heading
  const words = raw.split(/\s+/);
  if (words.length > 5) return null;
  if (RE.dateRange.test(raw)) return null;            // a role header, not a section
  const norm = raw.toLowerCase().replace(/[^a-z\s]/g, " ").replace(/\s+/g, " ").trim();
  if (!norm) return null;

  // Exact is the DEFAULT, and deliberately so. A LinkedIn export writes its
  // headings verbatim — "Experience", "Top Skills", "Summary" — and exact
  // matching was already correct there. Turning the loose matcher on for that
  // path moved five of nine cohort profiles and made three of them worse: a
  // software engineer became *Wind Energy Development Managers*, and one profile
  // lost its Experience section entirely (roles 4 → 0), because a headline or a
  // job title containing "Projects" or "Skills" became a section boundary.
  //
  // The loose matcher exists for Word CVs, where the headings are whatever the
  // author felt like. It should not be paid for by the path that already worked.
  const exact = SECTIONS.find((sec) => norm === sec.toLowerCase());
  if (exact) return exact;
  if (!fuzzy) return null;

  for (const [name, re] of SECTION_MATCHERS) if (re.test(norm)) return name;
  return null;
}

/** Split flat lines into { sectionName: string[] }. */
export function splitSections(lines, { fuzzy = false } = {}) {
  const out = {};
  let current = "_preamble";
  out[current] = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const hit = headingFor(line, fuzzy);
    if (hit) {
      current = hit;
      out[current] = out[current] || [];
      continue;
    }
    (out[current] = out[current] || []).push(line);
  }
  return out;
}


/**
 * Titles hiding in prose.
 *
 * A LinkedIn PDF gives us dated role blocks and the title sits on the line above
 * the date range. A Word CV gives us none of that — it is paragraphs, and the
 * job title is a phrase inside a sentence: `In her current role, as "Director-
 * Application owner", Sangeetha is leading...`. The docx path was extracting no
 * titles at all, which left context resolution with nothing but a bag of words
 * and produced *Search Marketing Strategists* for a technology delivery leader,
 * on the strength of "digital", "web" and "strategy".
 *
 * We pull candidate phrases from the three places a title actually appears, then
 * make the title index adjudicate. Nothing is invented: a candidate survives only
 * if O*NET recognises it, so the cost of a loose extractor is wasted work, never
 * a fabricated role.
 */
const ROLE_NOUN = /\b(director|manager|head|lead|leader|architect|engineer|analyst|consultant|specialist|officer|developer|administrator|scientist|designer|programmer|coordinator|supervisor|president|principal|partner|owner|strategist|advisor|controller)\b/i;

export function extractTitlesFromProse(text, resolver, { limit = 8 } = {}) {
  const src = String(text || "");
  const seen = new Set();
  const candidates = [];
  const offer = (raw) => {
    const t = String(raw || "")
      .replace(/[\u2018\u2019\u201c\u201d"']/g, " ")
      .replace(/[^A-Za-z0-9&/\- ]+/g, " ")
      .replace(/\s*[-/]\s*/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (t.length < 4 || t.length > 60) return;
    if (!ROLE_NOUN.test(t)) return;
    const words = t.split(" ");
    if (words.length > 6) return;
    const key = t.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    candidates.push(t);
  };

  // 1. Anything in quotes — how CVs mark a title mid-sentence.
  for (const m of src.matchAll(/[\u201c"']([^\u201d"']{3,60})[\u201d"']/g)) offer(m[1]);
  // 2. "as a Senior Delivery Manager," / "as Director of Engineering."
  for (const m of src.matchAll(/\bas\s+(?:an?|the)?\s*([A-Za-z][A-Za-z0-9&/\-, ]{3,60}?)(?=[,.;:(]|\s+(?:at|for|with|in|to)\b)/g)) offer(m[1]);
  // 3. Short standalone lines — a heading in a CV that lost its formatting.
  for (const line of src.split(/\n+/)) {
    const l = line.trim();
    if (l && l.split(/\s+/).length <= 6) offer(l);
  }

  // The index decides — but it decides how much a candidate WEIGHS, not whether
  // it is allowed to exist. An earlier version demanded an exact hit or 0.7,
  // which threw away "Director Application owner" at 0.55 — a reading the index
  // resolved to Computer and Information Systems Managers, the right answer.
  // Everything downstream re-ranks against evidence and escalates when unsure;
  // a weak title contributes weakly and costs nothing. Filtering hard here just
  // hides candidates from the machinery built to judge them.
  const kept = [];
  for (const t of candidates) {
    const res = resolver.resolve(t, { limit: 3 });
    if (res.confidence >= 0.35) kept.push({ title: t, confidence: res.confidence, via: res.via });
  }
  // Ordered by how strongly the index recognises them. This is NOT recency —
  // a Word CV gives us no dates to order roles by, and callers that apply a
  // recency curve to this list are asserting something the document never said.
  return kept.sort((a, b) => b.confidence - a.confidence).slice(0, limit);
}

/** Months of tenure implied by a date-range line, from LinkedIn's own parenthetical. */
export function durationMonths(line) {
  const m = RE.durationInline.exec(line);
  if (m) return Number(m[1]) * 12 + (m[2] ? Number(m[2]) : 0);
  const mo = RE.monthsOnly.exec(line);
  if (mo) return Number(mo[1]);
  const solo = /^\s*(\d+)\s+(?:year|yr)s?(?:\s+(\d+)\s+(?:month|mo)s?)?\s*$/i.exec(line);
  if (solo) return Number(solo[1]) * 12 + (solo[2] ? Number(solo[2]) : 0);
  const soloMo = /^\s*(\d+)\s+(?:month|mo)s?\s*$/i.exec(line);
  if (soloMo) return Number(soloMo[1]);
  return null;
}

const isNoise = (l) => RE.page.test(l) || RE.location.test(l) || RE.bullet.test(l);

/**
 * Extract structure from a LinkedIn profile export.
 *
 * @param {string[]} lines
 * @param {{resolve:(t:string)=>{confidence:number,matches:Array}}} resolver
 *        The O*NET title resolver — used here as a *recogniser*: whichever
 *        candidate lines resolve to an occupation are the job titles.
 */
export function parseLinkedIn(cols, resolver) {
  // Sections are resolved PER COLUMN. The sidebar owns Top Skills / Languages /
  // Certifications; the main column owns Summary / Experience / Education.
  // Mixing them is what scattered the skills block (see pdfExtract.js).
  const side = splitSections(cols.sidebar || []);
  const body = splitSections(cols.main || []);
  const sections = { ...side, ...body };
  const exp = body.Experience || [];

  // Top Skills is capped at 5 by LinkedIn and is the highest-quality free
  // signal in the document — self-declared, already normalised.
  const topSkills = (side["Top Skills"] || []).filter((l) => !isNoise(l) && l.length > 1).slice(0, 5);
  const otherSkills = ((side.Skills || []).concat(body.Skills || []))
    .filter((l) => !isNoise(l) && l.length > 1).slice(0, 30);

  // Tenure: prefer LinkedIn's own parentheticals; they are unambiguous and,
  // usefully, durations rather than dates (ADR-024).
  const durations = [];
  for (const l of exp) { const d = durationMonths(l); if (d != null) durations.push(d); }
  let totalMonths = durations.length ? Math.max(...durations) : 0;
  // Company-level roll-ups repeat sub-role spans, so the max is a safer floor
  // than the sum, which double-counts.

  // ── Date-anchored role blocks ───────────────────────────────────────────
  // Probing the cohort files showed one rule holds in every export:
  // THE LINE IMMEDIATELY BEFORE A DATE RANGE IS THE JOB TITLE.
  // Everything else varies — company placement, tenure roll-ups, pagination —
  // but that adjacency does not. Anchoring on dates turns an unbounded scan
  // (which kept matching company names) into a bounded lookup.
  const isPage = (l) => RE.page.test(l);
  const isDate = (l) => RE.dateRange.test(l);
  const isDur = (l) => RE.duration.test(l);
  const isLoc = (l) => RE.location.test(l);

  const blocks = [];
  for (let i = 0; i < exp.length; i++) {
    if (!isDate(exp[i])) continue;
    // Walk back past pagination and tenure roll-ups to the title.
    let t = i - 1;
    while (t >= 0 && (isPage(exp[t]) || isDur(exp[t]) || isLoc(exp[t]))) t--;
    if (t < 0) continue;
    // Titles sometimes carry decoration: "Director | Payments | EMEA".
    const title = exp[t].split("|")[0].replace(/[,;·•\s]+$/, "").trim();
    if (!title || title.length < 3 || title.length > 90) continue;

    // Company is the next non-anchor line above the title.
    let c = t - 1;
    while (c >= 0 && (isPage(exp[c]) || isDur(exp[c]))) c--;
    const company = c >= 0 && !isDate(exp[c]) ? exp[c] : null;

    // Description: lines after the date (and its location) until the next anchor.
    const desc = [];
    for (let d = i + 1; d < exp.length; d++) {
      if (isDate(exp[d]) || isDur(exp[d]) || isPage(exp[d])) break;
      if (isLoc(exp[d])) continue;
      desc.push(exp[d]);
      if (desc.length >= 8) break;
    }

    blocks.push({
      title, company, months: durationMonths(exp[i]),
      description: desc.join(" "), index: blocks.length,
    });
  }

  // Resolve each title once. Order is document order — LinkedIn lists most
  // recent first, so block 0 is the current role.
  const resolved = blocks.map((b) => {
    const r = resolver.resolve(b.title, { limit: 2 });
    return { ...b, confidence: r.confidence, via: r.via, match: r.matches[0] || null, alternates: r.matches };
  });

  return {
    source: "linkedin-pdf",
    sectionsFound: Object.keys(sections).filter((k) => k !== "_preamble"),
    titles: resolved.map((b) => b.title),
    orderedByRecency: true,
    roles: resolved.map((b) => ({
      title: b.title, company: b.company, months: b.months,
      occupation: b.match ? b.match.title : null, code: b.match ? b.match.code : null,
      confidence: b.confidence, via: b.via,
    })),
    // Block 0 in document order = current role.
    currentRole: resolved.length
      ? { ...resolved[0].match, title: resolved[0].match?.title, rawTitle: resolved[0].title,
          confidence: resolved[0].confidence, via: resolved[0].via, alternates: resolved[0].alternates }
      : null,
    skills: [...topSkills, ...otherSkills],
    topSkillsCount: topSkills.length,
    tenureMonths: totalMonths || null,
    durationsSeen: durations.length,
    // Summary is the person's own account of their work — the best available
    // task evidence. Capped and redacted downstream (ADR-024).
    // Role descriptions beat the Summary as task evidence: they are specific to
    // a role rather than a self-marketing paragraph. Summary is the fallback.
    taskText: (resolved.map((b) => b.description).filter(Boolean).join(" ")
      || (body.Summary || []).join(" ")).slice(0, 2000),
    // What we could NOT determine. This drives which questions get asked, and
    // it should shrink as extraction improves (ADR-025).
    gaps: [],
  };
}

/** Split a skills paragraph into skills. A Word CV writes them as one sentence. */
export function splitSkillLine(line) {
  return String(line || "")
    .split(/[,;·•|]+/)
    .map((t) => t.replace(/\.$/, "").trim())
    .filter((t) => t.length >= 2 && t.length <= 60);
}

/**
 * Parse a Word CV.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * This logic used to live inline in `src/lib/parseFile.js`, and a hand-copied
 * duplicate lived in `tests/_harness.mjs`. So the production entry point had
 * ZERO test coverage and a broken parser went green on 55 tests
 * (docs/TEST_STRATEGY.md G1). One function, two callers, from here on.
 *
 * ── Why it is date-anchored ────────────────────────────────────────────────
 * ADR-030 built the prose path on the premise that "a Word CV has no dated role
 * blocks to anchor on". That is false for most Word CVs and was false for both
 * résumés in the 2026-09-03 external test: they carry the same
 * `Month YYYY–Month YYYY` blocks LinkedIn does, and `RE.dateRange` already
 * matched every one of them. The branch simply never looked, then hardcoded
 * `tenureMonths: null` and told a fifteen-year director we could not find his
 * dates — three lines below his own dates.
 *
 * So: try the structured read first, exactly as the LinkedIn path does. The
 * prose reader stays as the fallback for CVs that genuinely have no dates,
 * which is the case ADR-030 was actually written for.
 */
export function parseDocxText(text, resolver, { now = new Date() } = {}) {
  const raw = String(text || "");
  const lines = raw.split("\n").map((l) => l.trim()).filter(Boolean);
  // fuzzy: a Word CV writes "PROFESSIONAL EXPERIENCE" / "CORE TECHNICAL SKILLS".
  const sections = splitSections(lines, { fuzzy: true });
  const sectionsFound = Object.keys(sections).filter((k) => k !== "_preamble");

  // Skills first — same in both paths.
  const skills = (sections.Skills || sections["Top Skills"] || [])
    .flatMap(splitSkillLine).slice(0, 30);

  // A recognised Experience section is the clean case. When there is none — a CV
  // with no headings at all, or headings we still do not recognise — scan the
  // whole document rather than abandoning structure. Education lines survive this
  // safely: "B.Sc. — Madurai Kamaraj University, 1997" carries a year, not a
  // RANGE, so it never anchors a block.
  const exp = sections.Experience?.length ? sections.Experience : lines;
  const dated = datedBlocks(exp, now);

  if (dated.length) {
    // Recency is knowable here, so use it. Most recent end first; a role still
    // running outranks any finished one.
    const ordered = [...dated].sort((a, b) => {
      if (a.range.current !== b.range.current) return a.range.current ? -1 : 1;
      const endA = a.range.end || { y: 9999, m: 12 };
      const endB = b.range.end || { y: 9999, m: 12 };
      return (endB.y - endA.y) || (endB.m - endA.m);
    });

    const resolved = ordered.map((b) => {
      const r = resolver.resolve(b.title, { limit: 2 });
      return { ...b, confidence: r.confidence, via: r.via, match: r.matches[0] || null, alternates: r.matches };
    });
    const top = resolved[0];

    return {
      source: "docx",
      sectionsFound,
      orderedByRecency: true,
      titles: resolved.map((b) => b.title),
      roles: resolved.map((b) => ({
        title: b.title, company: b.company, scope: b.scope, months: b.range.months,
        start: b.range.start, end: b.range.end, current: b.range.current,
        occupation: b.match ? b.match.title : null, code: b.match ? b.match.code : null,
        confidence: b.confidence, via: b.via,
      })),
      currentRole: {
        ...(top.match || {}), title: top.match ? top.match.title : null, rawTitle: top.title,
        company: top.company, confidence: top.confidence, via: top.via, alternates: top.alternates,
      },
      skills,
      topSkillsCount: 0,
      // Union, not sum: concurrent roles are two rows on a CV and one stretch of a
      // life. A gap between jobs is still correctly excluded.
      tenureMonths: unionMonths(resolved.map((b) => b.range), now) || null,
      durationsSeen: resolved.length,
      // Role descriptions only. Using the whole document floods the evidence bag
      // with education and contact lines, which is how a technology leader
      // matched on his university's town.
      taskText: resolved.map((b) => b.description).filter(Boolean).join(" ").slice(0, 2000)
        || (sections.Summary || []).join(" ").slice(0, 2000),
      gaps: [],
    };
  }

  // ── Fallback: no dates anywhere (ADR-030's actual case) ──────────────────
  const found = extractTitlesFromProse(raw, resolver);
  return {
    source: "docx-prose",
    sectionsFound,
    // NOT recency. `extractTitlesFromProse` returns recognition strength, and
    // ADR-030 is explicit that a caller applying a recency curve to this list is
    // asserting something the document never said. The flag exists so the
    // context resolver can stop doing exactly that.
    orderedByRecency: false,
    titles: found.map((f) => f.title),
    roles: found.map((f) => ({ title: f.title, company: null, months: null, confidence: f.confidence })),
    // Deliberately null. With no dates there is nothing that makes one of these
    // the CURRENT role, and guessing from index confidence is what diagnosed a
    // fifteen-year director as the job he left in 2014. Ask instead.
    currentRole: null,
    skills,
    topSkillsCount: 0,
    tenureMonths: null,
    durationsSeen: 0,
    taskText: lines.join(" ").slice(0, 2000),
    gaps: [],
  };
}

/**
 * Role blocks anchored on date lines.
 *
 * Two layouts, and real CVs use both:
 *
 *   SEPARATE   Director, Operations Transformation — Harbor National Bank
 *              January 2020–Present
 *              · bullet
 *
 *   INLINE     Principal Data Engineer — Lakehouse  |  Fidelity (FMR India)Sep 2022 – Jun 2026
 *              · bullet
 *
 * The first version handled only SEPARATE — it took the line above the date as
 * the title. On an INLINE CV that line is the previous role's last bullet, so
 * every role came out wrong even though all four date ranges were found. Note
 * also that the inline form often has no space before the month
 * ("(FMR India)Sep 2022"), which is why the date is located by index rather than
 * by splitting on whitespace.
 */
function datedBlocks(exp, now) {
  const anchors = [];
  for (let i = 0; i < exp.length; i++) {
    const range = parseDateRange(exp[i], now);
    if (!range) continue;
    const before = exp[i].slice(0, range.index).replace(/[\s|,;–—-]+$/, "").trim();
    // A header is INLINE when real text precedes the date on the same line.
    const inline = before.length >= 3;
    anchors.push({ i, range, inline, header: inline ? before : (i > 0 ? exp[i - 1] : null) });
  }

  const blocks = [];
  for (let k = 0; k < anchors.length; k++) {
    const a = anchors[k];
    if (!a.header) continue;
    const { title, company, scope } = splitTitleCompany(a.header);
    if (!title || title.length < 3 || title.length > 90) continue;

    // Description runs to the next role's header — which is the next date line
    // itself when that one is inline, and the line above it when it is not.
    const next = anchors[k + 1];
    const stop = next ? (next.inline ? next.i : next.i - 1) : exp.length;
    const body = exp.slice(a.i + 1, Math.max(a.i + 1, stop))
      .filter((l) => !RE.location.test(l)).join(" ");
    const description = [scope, body].filter(Boolean).join(". ");

    blocks.push({ title, company, scope, range: a.range, description });
  }
  return blocks;
}

/**
 * Total months worked, as the UNION of the role intervals rather than their sum.
 *
 * Summing double-counts concurrent roles, and concurrency is normal: two project
 * titles at one employer over the same four years is two rows on the CV and four
 * years of a life. The real résumé that surfaced this had two Principal Data
 * Engineer entries sharing a date range exactly — summing turned nineteen years
 * of experience into twenty-two.
 */
export function unionMonths(ranges, now = new Date()) {
  const spans = ranges
    .map((r) => {
      const end = r.end || { y: now.getFullYear(), m: now.getMonth() + 1 };
      return [r.start.y * 12 + r.start.m, end.y * 12 + end.m];
    })
    .filter(([a, b]) => b >= a)
    .sort((x, y) => x[0] - y[0]);
  if (!spans.length) return 0;

  let total = 0;
  let [curStart, curEnd] = spans[0];
  for (const [s2, e2] of spans.slice(1)) {
    if (s2 <= curEnd) { curEnd = Math.max(curEnd, e2); continue; }
    total += curEnd - curStart;
    [curStart, curEnd] = [s2, e2];
  }
  return total + (curEnd - curStart);
}

/** Populate `gaps` — the list that decides what we have to ask the user. */
export function assessGaps(parsed) {
  const gaps = [];
  if (!parsed.currentRole || parsed.currentRole.confidence < 0.55) gaps.push("current-role");
  if (!parsed.skills.length) gaps.push("skills");
  if (!parsed.tenureMonths) gaps.push("tenure");
  if (!parsed.taskText || parsed.taskText.length < 120) gaps.push("what-you-actually-do");
  return { ...parsed, gaps };
}
